import React from 'react';
import {
  DEFAULT_PROFILES,
  autoHideEmptyColumns, getFontConfig, getColumnWidths, getVisibleColumns,
} from '../../config/tableColumns';
import { priceBasisLabel } from '../../components/quotations/constants';
import { computeLineItemTotals, formatQtyTotal, formatWeightTotal } from '../../utils/lineItemTotals';

/**
 * Print-view items table for a Sales Order PDF.
 * Extracted from OrderPrintView so the outer page can stay focused on
 * letterhead / info-box / totals / terms layout — this component owns the
 * per-column render + font-scaling logic exclusively.
 *
 * Pure — no state, no data fetching. Consumers pass in the loaded order
 * plus the same currency/date formatters used elsewhere on the page so the
 * PDF stays visually consistent with the on-screen preview.
 */
export function OrderItemsTablePrint({ order, currency, formatDate, formatCurrency, formatCurrency4 }) {
  const items = order.items || [];
  let selectedIds = order.pdf_columns || DEFAULT_PROFILES.order;
  const hideEmpty = order.auto_hide_empty !== false;
  if (hideEmpty) selectedIds = autoHideEmptyColumns(items, selectedIds);
  const visibleCols = getVisibleColumns(selectedIds);
  const widths = getColumnWidths(selectedIds);
  const font = getFontConfig(visibleCols.length);

  const renderCellValue = (item, colId, idx) => {
    const netWeight = item.net_weight_grams || 0;
    const qty = item.quantity || 0;
    const isKg = item.uom === 'kgs';
    const uom = isKg ? 'Kg' : 'Pcs';
    const description = item.part_description || item.description || '-';
    const customerPartNo = item.customer_part_no;
    // Part-No fallback: customer PN → internal PN → '-'. Never silently substitute the description.
    const displayPartNo = customerPartNo || item.part_internal_no || '-';

    switch (colId) {
      case 'row_num':      return idx + 1;
      case 'part_no':      return displayPartNo;
      case 'description':  return description;
      case 'hs_code':      return item.hs_code || '-';
      case 'net_weight':   return netWeight ? netWeight.toFixed(2) : '-';
      case 'total_weight': { const tw = isKg ? qty : (netWeight * qty) / 1000; return tw > 0 ? tw.toFixed(2) : '-'; }
      case 'qty':          return qty.toLocaleString('en-IN', { maximumFractionDigits: isKg ? 2 : 0 });
      case 'uom':          return uom;
      case 'tool_cost':    return (item.tooling_cost || 0) > 0 ? formatCurrency(item.tooling_cost, currency) : '-';
      case 'sample_cost':  return (item.sample_cost || 0) > 0 ? formatCurrency(item.sample_cost, currency) : '-';
      case 'packing_qty':  return item.packing_qty ? Math.round(item.packing_qty).toLocaleString('en-IN') : '-';
      case 'box_size':     return item.box_size || '-';
      case 'ship_date':    return item.shipment_date ? formatDate(item.shipment_date) : '-';
      case 'remarks':      return item.remarks || '-';
      case 'sample_lt':    return item.sample_lead_time || '-';
      case 'mfg_lt':       return item.manufacturing_lead_time || '-';
      case 'price':        return `${formatCurrency4(item.unit_price, currency)} / ${priceBasisLabel(item)}`;
      case 'total':        return formatCurrency(item.line_total, currency);
      default:             return '-';
    }
  };

  const cellStyle = (col) => {
    const base = {
      textAlign: col.align,
      verticalAlign: 'top',
      padding: font.padding,
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
    };
    if (col.id === 'part_no') return { ...base, fontWeight: '600', fontFamily: 'monospace', whiteSpace: 'normal' };
    if (col.id === 'description') return { ...base, whiteSpace: 'normal' };
    return base;
  };

  const totals = computeLineItemTotals(items);
  const renderTotalCell = (colId) => {
    switch (colId) {
      case 'description':   return `TOTAL (${totals.count} item${totals.count === 1 ? '' : 's'})`;
      case 'qty':           return formatQtyTotal(totals);
      case 'total_weight':  return formatWeightTotal(totals);
      case 'tool_cost':     return totals.toolingCost ? formatCurrency(totals.toolingCost, currency) : '-';
      case 'sample_cost':   return totals.sampleCost ? formatCurrency(totals.sampleCost, currency) : '-';
      case 'total':         return formatCurrency(totals.lineTotal, currency);
      default:              return '';
    }
  };

  return (
    <table className="items-table" style={{ fontSize: font.body, tableLayout: 'fixed', width: '100%' }}>
      <thead>
        <tr>
          {visibleCols.map(col => (
            <th key={col.id} style={{ width: widths[col.id], textAlign: col.align, fontSize: font.header, padding: font.padding }}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => (
          <tr key={item.id || idx}>
            {visibleCols.map(col => (
              <td key={col.id} style={cellStyle(col)}>{renderCellValue(item, col.id, idx)}</td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
          {visibleCols.map(col => (
            <td key={col.id} style={{ ...cellStyle(col), borderTop: '2px solid #1e40af' }}>
              {renderTotalCell(col.id)}
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  );
}
